import React, { useMemo } from 'react';
import { Box, Button, Card, Chip, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { CheckCircle, PlayArrow, Undo, HourglassBottom, Timer } from '@mui/icons-material';
import { IItemPedidoKDS, StatusItemKDS, StatusKDS } from '../../interfaces/IKDS';
import { KDSTimer } from '../KDSTimer';
import { useKdsTokens } from '../../theme/kdsTokens';

interface KDSCardProps {
    pedidoUuid: string;
    numeroPedido?: number;
    tipoPedido?: string;
    /** Rodada do QR Code por mesa: a mesma conta gera um card por envio. */
    rodadaNumero?: number;
    itens: IItemPedidoKDS[];
    configuracaoExibicao: {
        tempoAlertaAmareloSegundos: number;
        tempoAlertaVermelhoSegundos: number;
    };
    onIniciarItem: (uuid: string) => void;
    onConcluirItem: (uuid: string) => void;
    onConcluirTodos: (pedidoUuid: string) => void;
    onReverterItem?: (uuid: string) => void;
    isSelected?: boolean;
    selectedItemUuid?: string;
    statusKDS?: StatusKDS;
}

/**
 * Card de pedido na estação.
 *
 * O desenho segue o painel apresentado no site: superfície do tema, faixa de
 * status na lateral, cabeçalho com número do pedido e cronômetro em pílula, e
 * cada item numa linha com um quadradinho de status na frente.
 *
 * A versão anterior era um bloco escuro com hexadecimais fixos — resolvia para
 * um estabelecimento só, mas ignorava o tema do cliente (a plataforma é
 * white-label) e o botão de tema claro/escuro. Agora tudo vem de `useKdsTokens`.
 */

const formatarDetalhe = (detalhe: { tipo: string; descricao: string }): string => {
    if (detalhe.tipo === 'borda') return `Borda: ${detalhe.descricao}`;
    if (detalhe.tipo === 'massa') return `Massa: ${detalhe.descricao}`;
    if (detalhe.tipo === 'sabor') return `Sabor: ${detalhe.descricao}`;
    return detalhe.descricao;
};

const ROTULO_STATUS_KDS: Record<StatusKDS, string> = {
    [StatusKDS.PARADO]: 'Aguardando',
    [StatusKDS.EM_ANDAMENTO]: 'Em preparo',
    [StatusKDS.CONCLUIDO]: 'Pronto',
};

/** Quadradinho de status na frente do item — o mesmo código visual do site. */
const MarcadorStatus: React.FC<{ status: StatusItemKDS; cor: string; fundo: string }> = ({ status, cor, fundo }) => {
    const icone =
        status === StatusItemKDS.PRONTO ? <CheckCircle sx={{ fontSize: 12 }} />
            : status === StatusItemKDS.EM_PREPARO ? <PlayArrow sx={{ fontSize: 13 }} />
                : status === StatusItemKDS.AGUARDANDO_DISPARO ? <Timer sx={{ fontSize: 12 }} />
                    : <HourglassBottom sx={{ fontSize: 12 }} />;

    return (
        <Box
            sx={{
                width: 18,
                height: 18,
                mt: '2px',
                borderRadius: 1,
                flexShrink: 0,
                display: 'grid',
                placeItems: 'center',
                backgroundColor: fundo,
                color: cor,
            }}
        >
            {icone}
        </Box>
    );
};

export const KDSCard: React.FC<KDSCardProps> = ({
    pedidoUuid, numeroPedido, tipoPedido, rodadaNumero, itens, configuracaoExibicao,
    onIniciarItem, onConcluirItem, onConcluirTodos, onReverterItem,
    isSelected, selectedItemUuid, statusKDS,
}) => {
    const tokens = useKdsTokens();

    const statusGeral = itens.every(i => i.status === StatusItemKDS.PRONTO)
        ? StatusItemKDS.PRONTO
        : itens.some(i => i.status === StatusItemKDS.EM_PREPARO)
            ? StatusItemKDS.EM_PREPARO
            : StatusItemKDS.AGUARDANDO;

    const corGeral = tokens.statusCor(statusGeral);
    const primeiroItem = itens[0];

    // Agrupa itens por produto
    const gruposPorProduto = useMemo(() => {
        const mapa = new Map<string, { produtoDescricao: string; quantidade: number; itens: IItemPedidoKDS[] }>();
        for (const item of itens) {
            if (!mapa.has(item.produtoUuid)) {
                mapa.set(item.produtoUuid, {
                    produtoDescricao: item.produtoDescricao,
                    quantidade: item.itemTipo === 'produto' ? item.quantidade : 1,
                    itens: [],
                });
            }
            const grupo = mapa.get(item.produtoUuid)!;
            if (item.itemTipo === 'produto' && item.quantidade > 1) {
                grupo.quantidade = item.quantidade;
            }
            grupo.itens.push(item);
        }
        return Array.from(mapa.values());
    }, [itens]);

    const usarModoAgrupado = gruposPorProduto.some(g => g.itens.length > 1) || itens.some(i => !!i.processoDescricao);

    const renderItemRow = (item: IItemPedidoKDS, comIndentacao: boolean, hideObservacoes = false, hideDetalhesProduto = false) => {
        const isItemSelected = item.uuid === selectedItemUuid;
        const isComplement = item.itemTipo !== 'produto';
        const pronto = item.status === StatusItemKDS.PRONTO;
        const corItem = tokens.statusCor(item.status);
        const label = isComplement
            ? (item.itemDescricao ? formatarDetalhe({ tipo: item.itemTipo ?? 'item', descricao: item.itemDescricao }) : item.produtoDescricao)
            : (item.processoDescricao ?? item.produtoDescricao);

        return (
            <Box key={item.uuid} sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1,
                pl: comIndentacao ? 1 : 0.25, pr: 0.25, py: 0.4, borderRadius: 1,
                background: isItemSelected ? tokens.selecaoFundo : 'transparent',
                outline: isItemSelected ? `1px solid ${tokens.selecao}` : 'none',
            }}>
                <MarcadorStatus status={item.status} cor={corItem} fundo={tokens.tenue(corItem, 0.14)} />

                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        sx={{
                            color: pronto ? tokens.textoFraco : tokens.texto,
                            fontSize: '0.85rem',
                            fontWeight: 700,
                            lineHeight: 1.35,
                            textDecoration: pronto ? 'line-through' : 'none',
                        }}
                    >
                        {comIndentacao ? '↳ ' : ''}{item.quantidade}x {label}
                    </Typography>
                    {isComplement && item.processoDescricao && (
                        <Typography sx={{ color: tokens.textoSecundario, fontSize: '0.75rem' }}>
                            ↳ {item.processoDescricao}
                        </Typography>
                    )}
                    {!isComplement && !hideDetalhesProduto && item.detalhesProduto?.map((detalhe, i) => (
                        <Typography key={i} sx={{ color: tokens.textoSecundario, fontSize: '0.75rem' }}>
                            ↳ {formatarDetalhe(detalhe)}
                        </Typography>
                    ))}
                    {!hideObservacoes && item.observacoes && (
                        <Typography sx={{ color: tokens.palette.warning.main, fontSize: '0.75rem', fontWeight: 600 }}>
                            ⚠ {item.observacoes}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                    {item.status === StatusItemKDS.AGUARDANDO_DISPARO && item.aguardaSubitens && (
                        <Typography sx={{ fontSize: '0.65rem', color: tokens.textoFraco, px: 0.5, fontStyle: 'italic' }}>
                            aguard. subitens
                        </Typography>
                    )}
                    {item.status === StatusItemKDS.AGUARDANDO && (
                        <Tooltip title="Iniciar">
                            <IconButton size="small" onClick={() => onIniciarItem(item.uuid)} sx={{ color: tokens.palette.primary.main }}>
                                <PlayArrow fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    )}
                    {item.status === StatusItemKDS.EM_PREPARO && (
                        <>
                            {onReverterItem && (
                                <Tooltip title="Desfazer">
                                    <IconButton size="small" onClick={() => onReverterItem(item.uuid)} sx={{ color: tokens.textoFraco }}>
                                        <Undo sx={{ fontSize: '0.9rem' }} />
                                    </IconButton>
                                </Tooltip>
                            )}
                            <Tooltip title="Concluir">
                                <IconButton size="small" onClick={() => onConcluirItem(item.uuid)} sx={{ color: tokens.palette.success.main }}>
                                    <CheckCircle fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                    {item.status === StatusItemKDS.PRONTO && onReverterItem && (
                        <Tooltip title="Desfazer">
                            <IconButton size="small" onClick={() => onReverterItem(item.uuid)} sx={{ color: tokens.textoFraco }}>
                                <Undo sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                        </Tooltip>
                    )}
                </Box>
            </Box>
        );
    };

    return (
        <Card
            elevation={0}
            sx={{
                width: '100%',
                background: tokens.card,
                border: `1px solid ${tokens.borda}`,
                // Faixa de status na lateral: mostra o estado do pedido sem
                // pintar o cabeçalho inteiro, que competia com o cronômetro.
                borderLeft: `3px solid ${corGeral}`,
                borderRadius: 2,
                overflow: 'hidden',
                ...(isSelected && {
                    outline: `2px solid ${tokens.selecao}`,
                    outlineOffset: '2px',
                }),
            }}
        >
            <Box sx={{
                px: 1.5, py: 1,
                borderBottom: `1px solid ${tokens.borda}`,
                backgroundColor: tokens.superficie,
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1,
            }}>
                <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 800, color: tokens.texto, fontSize: '1rem', lineHeight: 1.2 }}>
                        #{numeroPedido ?? pedidoUuid.slice(-6).toUpperCase()}
                        {rodadaNumero && (
                            <Typography component="span" sx={{ fontWeight: 700, color: tokens.textoSecundario, fontSize: '0.85rem', ml: 0.75 }}>
                                · Rodada {rodadaNumero}
                            </Typography>
                        )}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25, flexWrap: 'wrap' }}>
                        {tipoPedido && (
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: tokens.tipoPedidoCor(tipoPedido), textTransform: 'capitalize' }}>
                                {tipoPedido}
                            </Typography>
                        )}
                        {statusKDS && (
                            <Typography sx={{ fontSize: '0.72rem', color: tokens.textoSecundario }}>
                                {tipoPedido ? '· ' : ''}{ROTULO_STATUS_KDS[statusKDS]}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {primeiroItem && (
                    <KDSTimer
                        iniciadoEm={primeiroItem.iniciadoEm}
                        disparadoEm={primeiroItem.disparadoEm}
                        criadoEm={primeiroItem.criadoEm}
                        tempoEstimadoSegundos={primeiroItem.tempoEstimadoSegundos}
                        tempoAlertaAmareloSegundos={configuracaoExibicao.tempoAlertaAmareloSegundos}
                        tempoAlertaVermelhoSegundos={configuracaoExibicao.tempoAlertaVermelhoSegundos}
                    />
                )}
            </Box>

            <Box sx={{ p: 1.25 }}>
                {usarModoAgrupado ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {gruposPorProduto.map((grupo, gi) => {
                            const normDesc = (s: string) => s.replace(/^adc:\s*/i, '').trim().toLowerCase();
                            const produtoItens = grupo.itens.filter(i => i.itemTipo === 'produto');
                            const complementoItens = grupo.itens.filter(i => i.itemTipo !== 'produto');
                            const detalhesProduto = produtoItens.find(i => i.detalhesProduto?.length)?.detalhesProduto ?? [];
                            const complementoNorms = new Set(complementoItens.map(c => normDesc(c.itemDescricao ?? '')));
                            const detalhesExtras = detalhesProduto.filter(d => !complementoNorms.has(normDesc(d.descricao)));
                            const observacoesGrupo = grupo.itens.find(i => i.observacoes)?.observacoes;
                            return (
                                <Box key={gi}>
                                    <Typography sx={{ color: tokens.textoSecundario, fontSize: '0.8rem', fontWeight: 700, px: 0.25, mb: 0.25 }}>
                                        {grupo.quantidade}x {grupo.produtoDescricao}
                                    </Typography>
                                    {observacoesGrupo && (
                                        <Typography sx={{ color: tokens.palette.warning.main, fontSize: '0.75rem', fontWeight: 600, px: 0.25 }}>
                                            ⚠ {observacoesGrupo}
                                        </Typography>
                                    )}
                                    {detalhesExtras.map((detalhe, i) => (
                                        <Typography key={i} sx={{ color: tokens.textoSecundario, fontSize: '0.75rem', px: 0.25 }}>
                                            ↳ {formatarDetalhe(detalhe)}
                                        </Typography>
                                    ))}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                        {produtoItens.map(item => renderItemRow(item, true, true, true))}
                                        {complementoItens.map(item => renderItemRow(item, true, true, true))}
                                    </Box>
                                    {gi < gruposPorProduto.length - 1 && <Divider sx={{ borderColor: tokens.borda, mt: 0.75 }} />}
                                </Box>
                            );
                        })}
                    </Box>
                ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        {itens.map(item => renderItemRow(item, false))}
                    </Box>
                )}

                {statusGeral !== StatusItemKDS.PRONTO ? (
                    <Button
                        fullWidth
                        color="success"
                        variant="contained"
                        startIcon={<CheckCircle />}
                        onClick={() => onConcluirTodos(pedidoUuid)}
                        sx={{ mt: 1.25, fontWeight: 700, fontSize: '0.8rem', py: 0.75 }}
                    >
                        Tudo pronto
                    </Button>
                ) : (
                    <Chip
                        label="Pedido pronto"
                        size="small"
                        icon={<CheckCircle />}
                        sx={{
                            mt: 1.25,
                            width: '100%',
                            fontWeight: 700,
                            color: tokens.palette.success.main,
                            backgroundColor: tokens.tenue(tokens.palette.success.main, 0.14),
                            '& .MuiChip-icon': { color: tokens.palette.success.main },
                        }}
                    />
                )}
            </Box>
        </Card>
    );
};
